import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { AreaDetail, AreaStatus, DocumentDto, MonitoringListItem, MonitoringSettings } from '@tww/shared';
import { AREA_STATUS_LABELS, allowedTransitions, fmtCoords, fmtDate, fmtDateTime, fmtEur, fmtHa, fmtNum, needsReason, windowLabel } from '@tww/shared';
import { api, qs } from '../../api/client';
import { useLookups } from '../../api/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { FilesTab } from '../../ui/FilesTab';
import { Modal } from '../../ui/Modal';
import { ErrorState, FactCard, Facts, Note, Panel, Skeleton, StatusPill } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { Select, TextArea } from '../../ui/fields';
import { useToast } from '../../ui/Toast';
import { ReviewNote } from '../../ui/ReviewNote';
import { AreaStatusPill, MapsLink, plantingLabel } from './common';
import { AreaMonitoringPanel } from '../monitoring/AreaMonitoringPanel';

export function AreaDetailPage() {
  const { id } = useParams();
  const aid = Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') === 'dateien' ? 'dateien' : 'uebersicht';
  const { can } = useAuth();
  const lookups = useLookups();
  const q = useQuery({ queryKey: ['area', aid], queryFn: () => api.get<AreaDetail>(`/areas/${aid}`), enabled: Number.isFinite(aid) });
  const photos = useQuery({ queryKey: ['documents', 'area', aid], queryFn: () => api.get<{ items: DocumentDto[] }>(`/documents${qs({ entity_type: 'area', entity_id: aid })}`).then((r) => r.items), enabled: Number.isFinite(aid) });
  // gleiche Abfrage wie im Monitoring-Panel (geteilter Cache), hier nur für das Zeitfenster aus den Einstellungen
  const mon = useQuery({ queryKey: ['area-monitorings', aid], queryFn: () => api.get<{ items: MonitoringListItem[]; settings: MonitoringSettings }>(`/areas/${aid}/monitorings`), enabled: Number.isFinite(aid) && can('monitoring') });
  const [statusModal, setStatusModal] = useState(false);
  const write = can('areas', 'write');
  const newVisit = useMutation({
    mutationFn: () => api.post<{ id: number }>(`/areas/${aid}/site-visits`, {}),
    onSuccess: (v) => nav(`/protokoll/${v.id}`),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  if (q.isLoading) return <Skeleton rows={8} />;
  if (q.isError || !q.data) return <ErrorState error={q.error ?? new Error('Fläche nicht gefunden')} retry={() => q.refetch()} />;
  const a = q.data;
  const lk = (key: string, code: string | null) => (code ? (lookups.data?.find((l) => l.key === key)?.values?.find((v) => v.code === code)?.label ?? code) : '–');
  const setTab = (t: string) => {
    const n = new URLSearchParams(sp);
    if (t === 'uebersicht') n.delete('tab');
    else n.set('tab', t);
    setSp(n, { replace: true });
  };
  const draft = a.site_visits.find((v) => v.status === 'draft');
  const lkv = (key: string, code: string | null) => (code ? lk(key, code) : null);
  // Flächendaten: nur befüllte Zeilen
  const facts = ([
    ['Ort', [a.zip, a.city].filter(Boolean).join(' ')],
    ['Gemeinde', a.municipality],
    ['Gemarkung', a.district],
    ['Flur / Flurstück', [a.flur, a.parcel].filter(Boolean).join(' / ')],
    ['Abteilung', a.compartment],
    ['Koordinaten', fmtCoords(a.lat, a.lng)],
    ['Teilflächen', a.polygons.map((p) => `${p.label ?? p.seq} ${fmtCoords(p.lat, p.lng)}${p.hectares != null ? ` (${fmtHa(p.hectares)} ha)` : ''}`).join(' · ')],
    ['Waldbesitzart', lkv('ownership_types', a.ownership_type)],
    ['Zertifizierung', lkv('certifications', a.certification)],
    ['Wasserschutz', a.water_protection],
    ['Boden', a.soil],
    ['Wildschutz', lkv('wildlife_protection', a.wildlife_protection)],
    ['Schadereignis', a.ecological_need],
    ['Nachbarpotenzial', a.neighbour_potential],
    ['Rettungspunkt', a.rescue_point],
    ['Zuwegung', a.access_note],
    ['Parken', fmtCoords(a.parking_lat, a.parking_lng)],
    ['Pflanzung', a.planting_year ? `${plantingLabel(a)}${a.planted_at ? ` · ${fmtDate(a.planted_at)}` : ''}` : null],
  ] as [string, string | null | undefined][]).filter((x): x is [string, string] => Boolean(x[1]));
  const imgs = (photos.data ?? []).filter((d) => d.has_thumb).slice(0, 8);
  return (
    <>
      <div className="crumb">
        <Link to="/flaechen">Flächen</Link>
      </div>
      <div className="head">
        <div>
          <h1>
            {a.name}
            {a.archived_at && <span className="pill muted" style={{ marginLeft: 10, verticalAlign: 'middle' }}>archiviert</span>}
          </h1>
          <div className="sub">
            {[a.municipality, [a.state_label, a.region].filter(Boolean).join(', '), a.hectares != null ? `${fmtHa(a.hectares)} ha` : null, a.forester ? `Förster ${a.forester.display_name}` : null].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div className="actions">
          <AreaStatusPill status={a.status} />
          {write && (
            <button className="btn primary" type="button" onClick={() => (draft ? nav(`/protokoll/${draft.id}`) : newVisit.mutate())} disabled={newVisit.isPending}>
              <Icon name="doc" /> {draft ? 'Begehungsprotokoll fortsetzen' : 'Neues Begehungsprotokoll'}
            </button>
          )}
          {write && (
            <Link className="btn" to={`/flaechen/${a.id}/bearbeiten`}>
              <Icon name="edit" /> Bearbeiten
            </Link>
          )}
          {write && allowedTransitions(a.status).length > 0 && (
            <button className="btn" type="button" onClick={() => setStatusModal(true)}>
              Status setzen
            </button>
          )}
        </div>
      </div>
      {a.status === 'abgelehnt' && <Note kind="bad">Abgelehnt{a.rejection_reason ? `: ${a.rejection_reason}` : ''}. Protokolle und Fotos bleiben erhalten; die Fläche kann in den Pool zurück.</Note>}
      {a.status === 'inaktiv' && <Note>Inaktiv{a.rejection_reason ? `: ${a.rejection_reason}` : ''}.</Note>}
      {a.review_needed && <ReviewNote entity="areas" id={a.id} queryKey={['area', aid]} canWrite={write} />}

      <nav className="nav-tabs" aria-label="Flächenakte">
        <button type="button" className={tab === 'uebersicht' ? 'active' : ''} onClick={() => setTab('uebersicht')}>
          Übersicht
        </button>
        <button type="button" className={tab === 'dateien' ? 'active' : ''} onClick={() => setTab('dateien')}>
          Fotos und Dateien{photos.data?.length ? ` (${photos.data.length})` : ''}
        </button>
      </nav>

      {tab === 'dateien' ? (
        <div className="panel">
          <FilesTab entityType="area" entityId={a.id} canWrite={write} ownLabel="Dateien dieser Fläche" />
        </div>
      ) : (
        <>
          <Facts>
            <FactCard label="Größe und Bäume" value={a.hectares != null ? `${fmtHa(a.hectares)} ha` : '–'} detail={a.trees_planted ? `${fmtNum(a.trees_planted)} Bäume gepflanzt` : a.polygons.length > 1 ? `${a.polygons.length} Teilflächen` : 'noch keine Pflanzung erfasst'} />
            <FactCard label="Förster" value={a.forester ? a.forester.display_name : '–'} href={a.forester ? `/kontakte/${a.forester.id}` : undefined} detail={a.forester_contract_year ? `Vertrag liegt vor (${a.forester_contract_year})` : a.forester ? 'Vertrag fehlt' : 'noch nicht zugeordnet'} missing={Boolean(a.forester) && !a.forester_contract_year} />
            <FactCard label="Waldbesitzer" value={a.owner?.display_name ?? a.owner_name_text ?? '–'} href={a.owner ? `/kontakte/${a.owner.id}` : undefined} detail={lk('ownership_types', a.ownership_type) !== '–' ? lk('ownership_types', a.ownership_type) : undefined} />
            {a.monitoring.kind === 'secured' ? (
              <FactCard label="Monitoring" value="abgeschlossen" detail={a.secured_at ? `Kultur gesichert am ${fmtDate(a.secured_at)}` : 'Kultur gesichert'} />
            ) : (
              <FactCard label="Nächstes Monitoring" value={a.monitoring.next ? a.monitoring.next.slice(0, 4) : '–'} href={a.monitoring.id ? `/monitoring/${a.monitoring.id}` : undefined} missing={a.monitoring.kind === 'overdue'} detail={a.monitoring.kind === 'overdue' ? `überfällig · ${windowLabel(mon.data?.settings.window)}` : a.monitoring.kind === 'due' ? windowLabel(mon.data?.settings.window) : 'wird nach der Pflanzung terminiert'} />
            )}
          </Facts>
          <div className="grid g21">
            <div className="col">
              <Panel title="Flächendaten" action={<MapsLink lat={a.lat} lng={a.lng} />}>
                <div className="pb">
                  {facts.length ? (
                    <dl className="dl">
                      {facts.map(([k, v]) => (
                        <Fragment key={k}>
                          <dt>{k}</dt>
                          <dd>{v}</dd>
                        </Fragment>
                      ))}
                    </dl>
                  ) : (
                    <span className="mut">Noch keine Flächendaten</span>
                  )}
                </div>
              </Panel>
              <Panel title="Aktionen auf dieser Fläche">
                {a.actions.length ? (
                  <div className="tw">
                    <table>
                      <thead><tr><th>Datum</th><th>Aktion</th><th className="num">Bäume</th><th>Status</th></tr></thead>
                      <tbody>
                        {a.actions.map((x) => (
                          <tr key={x.id} className="row" onClick={() => nav(`/aktionen/${x.id}`)}>
                            <td>{fmtDate(x.date)}</td>
                            <td><Link className="link" to={`/aktionen/${x.id}`}>{x.label}</Link><span className="sm">{x.code}</span></td>
                            <td className="num">{fmtNum(x.trees)}</td>
                            <td><StatusPill code={x.status} label={x.status_label} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty">Noch keine Aktion auf dieser Fläche.</div>
                )}
              </Panel>
              <AreaMonitoringPanel area={a} />
            </div>
            <div className="col">
              <Panel title="Begehungsprotokolle">
                {a.site_visits.length ? (
                  <div className="tw">
                    <table>
                      <tbody>
                        {a.site_visits.map((v) => (
                          <tr key={v.id} className="row" onClick={() => nav(`/protokoll/${v.id}`)}>
                            <td>
                              <Link className="link" to={`/protokoll/${v.id}`}>{fmtDate(v.visited_at)}</Link>
                              <span className="sm">{[v.visited_by, v.forester_name].filter(Boolean).join(' · ')}</span>
                            </td>
                            <td>{v.status === 'final' ? <StatusPill code="bestaetigt" label="abgeschlossen" /> : <StatusPill code="warn" label="Entwurf" />}</td>
                            <td className="num small">{v.price_per_tree_max != null ? fmtEur(v.price_per_tree_max) : ''}</td>
                            <td>
                              <a className="icon-btn" href={`/api/v1/site-visits/${v.id}/print`} target="_blank" rel="noreferrer" title="Druckansicht" aria-label="Druckansicht" onClick={(e) => e.stopPropagation()}>
                                <Icon name="doc" />
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty">Noch kein Begehungsprotokoll</div>
                )}
              </Panel>
              <Panel title="Fotos" action={<button type="button" className="btn sm" onClick={() => setTab('dateien')}>Alle Dateien</button>}>
                <div className="pb">
                  {imgs.length ? (
                    <div className="photo-grid">
                      {imgs.map((d) => (
                        <a key={d.id} href={`/api/v1/documents/${d.id}/file`} target="_blank" rel="noreferrer">
                          <img src={`/api/v1/documents/${d.id}/thumb`} alt={d.original_name} />
                        </a>
                      ))}
                    </div>
                  ) : (
                    <span className="mut">Noch keine Fotos</span>
                  )}
                </div>
              </Panel>
              <Panel title="Akquisehistorie">
                {a.history.length ? (
                  <ul className="list hist">
                    {a.history.map((h, i) => (
                      <li key={i}>
                        <span className="k">{fmtDateTime(h.at)}</span>
                        <span>
                          {h.from ? `${h.from} → ` : ''}<strong>{h.to}</strong>
                          {h.reason && <span className="sm">{h.reason}</span>}
                          {h.user && <span className="sm">{h.user}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="empty">Noch keine Statuswechsel</div>
                )}
              </Panel>
              {a.notes && (
                <Panel title="Notizen">
                  <div className="pb" style={{ whiteSpace: 'pre-wrap' }}>{a.notes}</div>
                </Panel>
              )}
            </div>
          </div>
        </>
      )}
      {statusModal && <StatusModal area={a} onClose={() => setStatusModal(false)} onDone={() => { qc.invalidateQueries({ queryKey: ['area', aid] }); qc.invalidateQueries({ queryKey: ['areas'] }); setStatusModal(false); }} />}
    </>
  );
}

function StatusModal({ area, onClose, onDone }: { area: AreaDetail; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const options = allowedTransitions(area.status);
  const [to, setTo] = useState<AreaStatus | ''>(options[0] ?? '');
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.post(`/areas/${area.id}/status`, { status: to, reason: reason || null }),
    onSuccess: () => {
      toast('Status gesetzt');
      onDone();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <Modal
      title="Status setzen"
      subtitle={`${area.name} · aktuell ${AREA_STATUS_LABELS[area.status]}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Abbrechen</button>
          <button className="btn primary" type="button" disabled={!to || (needsReason(to) && !reason.trim()) || m.isPending} onClick={() => m.mutate()}>Übernehmen</button>
        </>
      }
    >
      <div className="form one">
        <Select label="Neuer Status" value={to} onChange={(v) => setTo(v as AreaStatus)} options={options.map((s) => ({ value: s, label: AREA_STATUS_LABELS[s] }))} placeholder="wählen" />
        {to && needsReason(to) && <TextArea label="Grund" value={reason} onChange={setReason} rows={3} hint="Der Grund bleibt in der Akquisehistorie sichtbar" />}
        {to === 'pool' && (area.status === 'abgelehnt' || area.status === 'inaktiv') && <Note kind="info">Die frühere Ablehnung bleibt in der Historie sichtbar.</Note>}
      </div>
    </Modal>
  );
}
